// Native Chrome/OS notifications for the web app.
//
// Deliberately NOT Web Push: there is no service worker and no VAPID keypair.
// These fire only while a tab is open, driven by the Supabase Realtime insert
// listener in use-notifications.ts. That is enough to reach a user whose tab is
// backgrounded or whose browser is minimised, which is the gap the in-app
// sonner toast leaves.
import type { Notification as AppNotification } from '@task-tracker/shared';

export type DesktopNotificationPermission = NotificationPermission | 'unsupported';

const ENABLED_KEY_PREFIX = 'cs.desktop-notifications';
const PROMPT_DISMISSED_KEY_PREFIX = 'cs.desktop-notifications-prompt-dismissed';

/** The app icon Chrome renders beside the notification text. */
const NOTIFICATION_ICON = '/favicon-192.png';

export function isDesktopNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function getDesktopNotificationPermission(): DesktopNotificationPermission {
  if (!isDesktopNotificationSupported()) return 'unsupported';
  return window.Notification.permission;
}

/**
 * Must be called from a user gesture. Chrome silently (and permanently) denies
 * permission requests that aren't tied to one, so there is no auto-prompt
 * anywhere in the app — only the Settings toggle and the bell-dropdown banner.
 */
export async function requestDesktopNotificationPermission(): Promise<DesktopNotificationPermission> {
  if (!isDesktopNotificationSupported()) return 'unsupported';
  try {
    return await window.Notification.requestPermission();
  } catch {
    // Older Safari-style callback API, or a browser that rejects the request.
    return getDesktopNotificationPermission();
  }
}

// ── Per-user opt-in (localStorage) ──────────────────────────────────────────
//
// Not a server `notification_preferences` row: those channels (in_app / push /
// email) are account-wide, whereas OS-notification permission is granted per
// browser install. A server flag would claim "on" on a machine that never
// granted permission.

function enabledKey(userId: string | undefined): string {
  return `${ENABLED_KEY_PREFIX}.${userId ?? 'anon'}`;
}

function promptDismissedKey(userId: string | undefined): string {
  return `${PROMPT_DISMISSED_KEY_PREFIX}.${userId ?? 'anon'}`;
}

function readFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    // Private mode / blocked storage — treat as not set rather than throwing
    // inside a render or a Realtime callback.
    return false;
  }
}

function writeFlag(key: string, value: boolean): void {
  try {
    if (value) window.localStorage.setItem(key, 'true');
    else window.localStorage.removeItem(key);
  } catch {
    // Nothing to do: the toggle simply won't persist across reloads.
  }
}

/** Off by default — desktop notifications are strictly opt-in. */
export function isDesktopNotificationEnabled(userId: string | undefined): boolean {
  return readFlag(enabledKey(userId));
}

export function setDesktopNotificationEnabled(userId: string | undefined, enabled: boolean): void {
  writeFlag(enabledKey(userId), enabled);
}

export function isDesktopNotificationPromptDismissed(userId: string | undefined): boolean {
  return readFlag(promptDismissedKey(userId));
}

export function dismissDesktopNotificationPrompt(userId: string | undefined): void {
  writeFlag(promptDismissedKey(userId), true);
}

// ── Delivery ────────────────────────────────────────────────────────────────

/**
 * Shows one native notification.
 *
 * Returns whether it actually fired, so the caller can fall back to the in-app
 * toast instead of silently dropping the alert when permission is missing, the
 * row has no title, or the constructor throws.
 */
export function showDesktopNotification(
  notification: Pick<AppNotification, 'id' | 'title' | 'body'>,
  onClick: () => void,
): boolean {
  if (!isDesktopNotificationSupported()) return false;
  if (window.Notification.permission !== 'granted') return false;
  // A titleless row would otherwise surface an OS notification reading
  // "undefined". Returning false routes it to the toast fallback for free.
  if (!notification.title) return false;

  try {
    const native = new window.Notification(notification.title, {
      body: notification.body ?? undefined,
      icon: NOTIFICATION_ICON,
      // Chrome collapses same-tag notifications into one, which is what stops
      // two open tabs from double-notifying for a single insert.
      tag: notification.id,
      data: { notificationId: notification.id },
    });

    native.onclick = () => {
      window.focus();
      native.close();
      onClick();
    };

    return true;
  } catch {
    // Constructing a Notification throws on platforms that require a service
    // worker (Android Chrome). It must never break the Realtime handler.
    return false;
  }
}
